import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FiscalYear } from '../types/fiscal-year';

interface FiscalYearState {
  currentFiscalYear: FiscalYear | null;
  isSyncingToBackend: boolean;
  lastSyncError: string | null;
  setCurrentFiscalYear: (fiscalYear: FiscalYear | null, skipBackendSync?: boolean) => void;
}

export const useFiscalYearStore = create<FiscalYearState>()(
  persist(
    (set, get) => ({
      currentFiscalYear: null,
      isSyncingToBackend: false,
      lastSyncError: null,
      
      setCurrentFiscalYear: (fiscalYear, skipBackendSync = false) => {
        const prev = get().currentFiscalYear;
        set({ currentFiscalYear: fiscalYear, lastSyncError: null });
        
        // Si l'exercice a réellement changé
        if (prev?.id !== fiscalYear?.id) {
          // Invalider les caches locaux
          invalidateAllCachesOnFYChange();
          
          // Notifier le backend du changement (sauf si demandé de skip, ex: init depuis backend)
          if (!skipBackendSync) {
            syncFiscalYearToBackend(fiscalYear?.id || null, set);
          }
        }
      },
    }),
    {
      name: 'fiscal-year-storage',
      // Ne pas persister l'état de sync
      partialize: (state) => ({ currentFiscalYear: state.currentFiscalYear }),
    }
  )
);

/**
 * Synchronise le choix d'exercice fiscal vers le backend.
 * Permet la persistance multi-appareils des préférences utilisateur.
 * 
 * @param fiscalYearId - ID de l'exercice fiscal (ou null)
 * @param set - Fonction de mise à jour du store
 */
async function syncFiscalYearToBackend(
  fiscalYearId: string | null, 
  set: (state: Partial<FiscalYearState>) => void
) {
  set({ isSyncingToBackend: true, lastSyncError: null });
  
  try {
    // Import dynamique pour éviter dépendance circulaire
    const { usersApi } = await import('../services/api/endpoints/users');
    
    const response = await usersApi.updateCurrentFiscalYear(fiscalYearId);
    
    if (response.success) {
      console.log('✅ Exercice fiscal synchronisé avec le backend:', fiscalYearId);
    } else {
      console.warn('⚠️ Échec sync exercice fiscal:', response.error);
      set({ lastSyncError: response.error || 'Erreur lors de la synchronisation' });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('❌ Erreur sync exercice fiscal:', errorMessage);
    set({ lastSyncError: errorMessage });
    // Note: On ne bloque pas l'UI, le choix local reste valide
  } finally {
    set({ isSyncingToBackend: false });
  }
}

/**
 * Invalidation globale des caches quand l'exercice fiscal change.
 * Utilise des imports dynamiques pour éviter les dépendances circulaires.
 */
async function invalidateAllCachesOnFYChange() {
  console.log('🔄 Exercice fiscal changé — invalidation de tous les caches');

  // Invalider le cache in-memory de ContextService
  try {
    const { ContextService } = await import('../services/ContextService');
    ContextService.invalidateCache();
  } catch (_) { /* ignore si le module n'existe pas */ }

  // Invalider le cache in-memory de ChartsApiService
  try {
    const { ChartsApiService } = await import('../services/chartsApi');
    ChartsApiService.clearCache();
  } catch (_) { /* ignore si le module n'existe pas */ }

  // Nettoyer les données dashboard de IndexedDB (elles sont scopées par FY)
  try {
    const { IndexedDBService } = await import('../services/storage/IndexedDBService');
    await IndexedDBService.clear('dashboard_data');
    await IndexedDBService.clear('dashboard_timeseries');
  } catch (_) { /* ignore */ }
}